import React, { ComponentType } from 'react';
import { useLocation } from '@tanstack/react-router';
import {
  Layout,
  Stack,
  Card,
  AutoSavedTagsInputControl,
  Text,
  Information,
  Trans,
} from '@piwikpro/ui-components';
import { CopyToClipboard, SPLIT_SEPARATORS, withInject } from '@piwikpro/platform';
import { FormValidator } from '@piwikpro/form-crate';
import { TrackerSettings, TrackerSettingsResponse } from '../../../TrackerSettings';
import FailMessage from '../FailMessage';
import StripSiteSearchQueryParameters from '../../GlobalSettings/StripSiteSearchQueryParameters';
import KeepUrlFragment from '../../GlobalSettings/KeepUrlFragment';
import CampaignNameParams from '../../GlobalSettings/CampaignNameParams';
import CampaignKeywordParams from '../../GlobalSettings/CampaignKeywordParams';
import CampaignSourceParams from '../../GlobalSettings/CampaignSourceParams';
import CampaignIdParams from '../../GlobalSettings/CampaignIdParams';
import CampaignContentParams from '../../GlobalSettings/CampaignContentParams';
import CampaignMediumParams from '../../GlobalSettings/CampaignMediumParams';
import NewSessionOnCampaignChange from '../../GlobalSettings/NewSessionOnCampaignChange';
import ExcludeCrawlers from '../../GlobalSettings/ExcludeCrawlers';
import ExcludeUnknownUrl from '../../GlobalSettings/ExcludeUnknownUrl';
import NewSessionOnReferrerChange from '../../GlobalSettings/NewSessionOnReferrerChange';
import MaxSessionEventCount from '../../GlobalSettings/MaxSessionEventCount';
import MaxSessionTime from '../../GlobalSettings/MaxSessionTime';
import ActionOnSessionLimitExceeded from '../../GlobalSettings/ActionOnSessionLimitExceeded';

export interface GlobalDataCollectionSettingsProps {
  settings: TrackerSettingsResponse
  failed: boolean
  trackerSettings: TrackerSettings
  Title: ComponentType<any>
  validation: FormValidator
  t: Function
}

const DataCollectionTitle: React.FC<{
  Title: ComponentType<any>
  t: Function
  children: React.ReactNode
}> = ({ Title, t, children }) => {
  const { pathname } = useLocation();

  return (
    <Title
      part={t('platform.dataCollectionSettings.title')}
      update={pathname.includes('/data-collection')}
    >
      {children}
    </Title>
  );
};

export class GlobalDataCollectionSettings extends React.Component<
GlobalDataCollectionSettingsProps
> {
  handleUpdateSettings = async (values: any, {
    setErrors,
    setSubmitting,
    setFieldValue,
  }: any) => {
    const { trackerSettings, settings } = this.props;

    try {
      await trackerSettings.updateSettings(values);
    } catch (err: any) {
      setErrors(err.errors);
      setFieldValue(
        Object.keys(values)[0],
        (settings.attributes as any)[Object.keys(values)[0]],
        false,
      );
    }

    setSubmitting(false);
  }

  render() {
    const {
      Title,
      failed,
      settings,
      validation,
      t,
    } = this.props;

    return (
      <DataCollectionTitle Title={Title} t={t}>
        <Layout>
          <Layout.AnnotatedSection
            title={t('apps.details.data-collection.internal-search-engine.category')}
          />
          <Layout.Column dominant>
            <Card>
              <Card.Section>
                <Stack vertical spacing="wide">
                  <Stack.Item fill>
                    <Stack vertical spacing="narrow">
                      <Stack.Item>
                        <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.data-collection.internal-search-engine.title')}</Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Text as="p">{t('apps.details.data-collection.internal-search-engine.description')}</Text>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item fill>
                    <Information type="info">{t('apps.details.data-collection.internal-search-engine.help')}</Information>
                  </Stack.Item>
                </Stack>
              </Card.Section>
              <Card.Section>
                {failed ? <FailMessage /> : (
                  <>
                    <Stack vertical spacing="narrow" horizontalAlignment="fill">
                      <Stack.Item>
                        <AutoSavedTagsInputControl
                          autoFocus
                          id="tracker-settings-tracking-query-params"
                          label={t('apps.details.settings.tracking.site-search.query-params.title')}
                          info={(
                            <div style={{ display: 'inline-block' }}>
                              <Trans
                                i18nKey="apps.details.settings.tracking.site-search.query-params.info"
                                components={[
                                  <b>text</b>,
                                ]}
                              />
                            </div>
                          )}
                          name="site_search_query_params"
                          initialValue={settings.attributes.site_search_query_params}
                          validationSchema={validation.createValidationSchema(
                            (validators: any) => ({
                              site_search_query_params: validators.apps.siteSearchQueryParams,
                            }),
                          )}
                          splitSeparators={SPLIT_SEPARATORS}
                          onSubmit={this.handleUpdateSettings}
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <CopyToClipboard text={settings.attributes.site_search_query_params?.join('\n')} />
                      </Stack.Item>
                      <Stack.Item previousSpacing="wide">
                        <AutoSavedTagsInputControl
                          id="tracker-settings-tracking-categories"
                          label={t('apps.details.settings.tracking.site-search.query-category.title')}
                          info={(
                            <div style={{ display: 'inline-block' }}>
                              <Trans
                                i18nKey="apps.details.settings.tracking.site-search.query-category.info"
                                components={[
                                  <b>text</b>,
                                ]}
                              />
                            </div>
                          )}
                          name="site_search_category_params"
                          initialValue={settings.attributes.site_search_category_params}
                          validationSchema={validation.createValidationSchema(
                            (validators: any) => ({
                              site_search_category_params: (
                                validators.apps.siteSearchCategoryParams
                              ),
                            }),
                          )}
                          splitSeparators={SPLIT_SEPARATORS}
                          onSubmit={this.handleUpdateSettings}
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <CopyToClipboard text={settings.attributes.site_search_category_params?.join('\n')} />
                      </Stack.Item>
                    </Stack>
                  </>
                )}
              </Card.Section>
            </Card>
            <Card>
              <StripSiteSearchQueryParameters />
            </Card>
          </Layout.Column>
        </Layout>
        <Layout>
          <Layout.AnnotatedSection
            title={t('apps.details.settings.blacklisting.exclude-url-params.category')}
          />
          <Layout.Column dominant>
            <Card>
              <Card.Section>
                <Stack vertical>
                  <Stack.Item fill>
                    <Stack vertical spacing="narrow">
                      <Stack.Item>
                        <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.blacklisting.exclude-url-params.title')}</Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Text as="p">{t('apps.details.settings.blacklisting.exclude-url-params.description')}</Text>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  {failed ? <Stack.Item fill><FailMessage /></Stack.Item> : (
                    <>
                      <Stack.Item fill>
                        <AutoSavedTagsInputControl
                          id="tracker-settings-blacklisting-url-params"
                          label={t('apps.details.settings.blacklisting.exclude-url-params.label')}
                          info={`${t('apps.details.settings.blacklisting.exclude-url-params.help')} ${t('components:tagsInput.placeholder.default')}`}
                          name="url_query_parameter_to_exclude_from_url"
                          initialValue={settings.attributes.url_query_parameter_to_exclude_from_url}
                          validationSchema={validation.createValidationSchema(
                            (validators: any) => ({
                              // eslint-disable-next-line max-len
                              url_query_parameter_to_exclude_from_url: validators.apps.excludedUrlParams,
                            }),
                          )}
                          splitSeparators={SPLIT_SEPARATORS}
                          onSubmit={this.handleUpdateSettings}
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <CopyToClipboard text={settings.attributes.url_query_parameter_to_exclude_from_url?.join('\n')} />
                      </Stack.Item>
                    </>
                  )}
                </Stack>
              </Card.Section>
            </Card>
            <Card>
              <KeepUrlFragment />
            </Card>
          </Layout.Column>
        </Layout>
        <Layout>
          <Layout.AnnotatedSection
            title={t('apps.details.settings.campaign.title')}
          />
          <Layout.Column dominant>
            <Card>
              <Card.Section>
                <Stack vertical spacing="narrow">
                  <Stack.Item>
                    <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.campaign-parameters.title')}</Text>
                  </Stack.Item>
                  <Stack.Item>
                    <Text as="p">{t('apps.details.settings.campaign-parameters.description')}</Text>
                  </Stack.Item>
                </Stack>
              </Card.Section>
              {failed ? <Card.Section><FailMessage /></Card.Section> : (
                <>
                  <CampaignNameParams />
                  <CampaignKeywordParams />
                  <CampaignSourceParams />
                  <CampaignMediumParams />
                  <CampaignContentParams />
                  <CampaignIdParams />
                </>
              )}
            </Card>
            <Card>
              <NewSessionOnCampaignChange />
            </Card>
          </Layout.Column>
        </Layout>
        <Layout>
          <Layout.AnnotatedSection
            title={t('apps.details.settings.filters.title')}
          />
          <Layout.Column dominant>
            <Card>
              <Card.Section>
                <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.blacklisting.exclude-ips.title')}</Text>
                <Stack vertical>
                  {failed ? <Stack.Item fill><FailMessage /></Stack.Item> : (
                    <>
                      <Stack.Item fill>
                        <AutoSavedTagsInputControl
                          id="tracker-settings-blacklisting-ips"
                          label="&nbsp;"
                          info={t('apps.details.settings.blacklisting.exclude-ips.help')}
                          name="excluded_ips"
                          initialValue={settings.attributes.excluded_ips}
                          validationSchema={validation.createValidationSchema(
                            (validators: any) => ({
                              excluded_ips: validators.apps.excludedIps,
                            }),
                          )}
                          splitSeparators={SPLIT_SEPARATORS}
                          onSubmit={this.handleUpdateSettings}
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <CopyToClipboard text={settings.attributes.excluded_ips?.join('\n')} />
                      </Stack.Item>
                    </>
                  )}
                </Stack>
              </Card.Section>
            </Card>
            <Card>
              <ExcludeCrawlers />
            </Card>
            <Card>
              <Card.Section>
                <Stack vertical spacing="narrow">
                  <Stack.Item>
                    <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.filters.add-crawlers.title')}</Text>
                  </Stack.Item>
                  <Stack.Item>
                    <Text as="p">{t('apps.details.settings.filters.add-crawlers.description')}</Text>
                  </Stack.Item>
                </Stack>
                <Stack vertical>
                  {failed ? <Stack.Item fill><FailMessage /></Stack.Item> : (
                    <>
                      <Stack.Item fill>
                        <AutoSavedTagsInputControl
                          id="tracker-settings-blacklisting-user-agents"
                          label="&nbsp;"
                          info={`${t('apps.details.settings.blacklisting.exclude-ua.help')} ${t('components:tagsInput.placeholder.default')}`}
                          name="excluded_user_agents"
                          initialValue={settings.attributes.excluded_user_agents}
                          validationSchema={validation.createValidationSchema(
                            (validators: any) => ({
                              excluded_user_agents: validators.apps.excludedUserAgents,
                            }),
                          )}
                          onSubmit={this.handleUpdateSettings}
                        />
                      </Stack.Item>
                      <Stack.Item>
                        <CopyToClipboard text={settings.attributes.excluded_user_agents?.join('\n')} />
                      </Stack.Item>
                    </>
                  )}
                </Stack>
              </Card.Section>
            </Card>
            <Card>
              <ExcludeUnknownUrl />
            </Card>
          </Layout.Column>
        </Layout>
        <Layout>
          <Layout.AnnotatedSection
            title={t('apps.details.settings.session-limits.title')}
          />
          <Layout.Column dominant>
            <Card>
              <Card.Section>
                <Stack vertical spacing="narrow">
                  <Stack.Item>
                    <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.session-limits.title')}</Text>
                  </Stack.Item>
                  <Stack.Item>
                    <Text as="p">{t('apps.details.settings.session-limits.description')}</Text>
                  </Stack.Item>
                </Stack>
              </Card.Section>
              <Card.Section>
                <MaxSessionTime />
              </Card.Section>
              <Card.Section>
                <MaxSessionEventCount />
              </Card.Section>
              <Card.Section>
                <ActionOnSessionLimitExceeded />
              </Card.Section>
            </Card>
          </Layout.Column>
        </Layout>
        <Layout>
          <Layout.AnnotatedSection
            title={t('apps.details.settings.other-options.title')}
          />
          <Layout.Column dominant>
            <Card>
              <NewSessionOnReferrerChange />
            </Card>
          </Layout.Column>
        </Layout>
      </DataCollectionTitle>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
  validation: 'FormCrate.validation',
  Title: 'TitleCrate.components.Title',
})(GlobalDataCollectionSettings as any);
